/**
 * =============================================================================
 * theme.js
 * =============================================================================
 * Entry point for all theme Javascript.
 *
 * This file is responsible for wiring together:
 * - Vendor lazy-loading (3rd-party libraries that are not needed on every page)
 * - Extension registration (core eager extensions + heavyweight lazy extensions)
 * - Custom element autoloading (define only the elements that appear in the DOM)
 * - Global helpers (small utilities that some legacy scripts rely on)
 * - Bootstrapping Stencil (create instance, expose statics, call init)
 *
 * Bundling intent (Webpack):
 * - Main bundle should contain:
 *   - Stencil core runtime
 *   - Truly global vendors (whatever remains in vendor.js)
 *   - Core extensions (lightweight, needed site-wide)
 *
 * - Separate async chunks should contain:
 *   - Heavy vendors (Swiper, PhotoSwipe, AOS, TomSelect, etc.)
 *   - Heavy/contextual extensions (PropositionsManager, ShopifyStorefrontApi, etc.)
 *   - Custom elements (split per element file)
 * =============================================================================
 */

// Vendor JS, from node_modules (whatever is still truly global)
import "./vendor.js";

// Project JS
import Stencil from "./stencil.js";

/**
 * Create the Stencil runtime and expose it as `window.Stencil`.
 *
 * Notes:
 * - Stencil's constructor currently does an Object.assign(this, window.Stencil) to support
 *   rehydration/migration in some scenarios, so we always create the instance first.
 */
function setupStencil() {
  window.Stencil = new Stencil();
  return window.Stencil;
}

/**
 * Expose static methods on the Stencil class onto the Stencil instance.
 *
 * This is legacy convenience so code can call:
 *   Stencil.formatMoney(...)
 * as:
 *   window.Stencil.formatMoney(...)
 *
 * This keeps older code working while we gradually standardise on instance methods vs statics.
 */
function exposeStencilStatics(StencilClass, instance) {
  Object.getOwnPropertyNames(StencilClass)
    .filter((key) => typeof StencilClass[key] === "function")
    .forEach((key) => {
      instance[key] = StencilClass[key];
    });
}

/**
 * Register lazy vendor loaders.
 *
 * Each vendor is registered under a name and only downloaded when needed
 * via `await window.Stencil.ensureVendor("<name>")`.
 *
 * Pattern:
 * - Guard: if window.<Vendor> exists, return it (prevents duplicate work)
 * - import() vendor module(s)
 * - do any required one-time setup
 * - cache handle on window for debugging / legacy integrations
 */
function registerVendors(stencil) {
  stencil.registerVendor("swiper", async () => {
    if (window.Swiper) return window.Swiper;
  
    await stencil.ensureStylesheet(window.StencilAssets?.css?.swiper, "css-vendor-swiper");

    const mod = await import(
      /* webpackChunkName: "vendor.swiper" */
      "./vendors/swiper"
    );
  
    window.Swiper = mod.default;
    window.SwiperModules = mod.SWIPER_MODULES; // optional, but convenient
    return window.Swiper;
  });

  stencil.registerVendor("photoswipe", async () => {
    if (window.PhotoSwipe) return window.PhotoSwipe;

    await stencil.ensureStylesheet(window.StencilAssets?.css?.photoswipe, "css-vendor-photoswipe");

    const mod = await import(/* webpackChunkName: "vendor.photoswipe" */ "photoswipe");
    window.PhotoSwipe = mod.default;
    return window.PhotoSwipe;
  });

  stencil.registerVendor("tom-select", async () => {
    if (window.TomSelect) return window.TomSelect;

    await stencil.ensureStylesheet(window.StencilAssets?.css?.tomSelect, "css-vendor-tomselect");

    const mod = await import(/* webpackChunkName: "vendor.tom-select" */ "tom-select");
    // Some modules export default, some export module object. This handles both.
    window.TomSelect = mod.default || mod;
    return window.TomSelect;
  });

  stencil.registerVendor("aos", async () => {
    if (window.AOS) return window.AOS;

    await stencil.ensureStylesheet(window.StencilAssets?.css?.aos, "css-vendor-aos");

    const mod = await import(/* webpackChunkName: "vendor.aos" */ "aos");
    const AOS = mod.default || mod;

    window.AOS = AOS;
    return AOS;
  });
}

/**
 * Register lazy extension loaders.
 *
 * Extensions are Stencil-managed feature classes, typically stored under:
 *   window.Stencil.Extensions.<Name>
 *
 * For heavyweight or contextual extensions, we register a loader here and then
 * call `await window.Stencil.ensureExtension("<Name>")` from Liquid snippets or
 * from other extensions when needed.
 *
 * Important:
 * - If an extension file is included in a sync require.context, webpack will bundle
 *   it into the main chunk and you will not see separate ext.* chunk files.
 * - This is why `registerExtensions()` below excludes these files by regex.
 */
function registerExtensionLoaders(stencil) {
  stencil.registerExtensionLoader("PropositionsManager", async () => {
    // Debug only: confirm chunk import path is executed
    console.log("[Loader] importing PropositionsManager chunk");
    return import(
      /* webpackChunkName: "ext.propositions-manager" */
      "./extensions/propositions-manager"
    );
  });

  stencil.registerExtensionLoader(
    "ShopifyStorefrontApi",
    () =>
      import(
        /* webpackChunkName: "ext.shopify-storefront-api" */
        "./extensions/shopify-storefront-api"
      )
  );
}

/**
 * Custom element autoloading
 *
 * Instead of eagerly importing/defining every custom element, we:
 * 1) Scan the DOM for custom tags (<something-with-a-dash>)
 * 2) For each tag, attempt a dynamic import from ./elements/<tag>
 * 3) Define the custom element if the module exists
 * 4) Watch the DOM for injected HTML and repeat for new tags
 *
 * This enables "section-driven" behaviour: sections can add different elements per page,
 * and we only pay for the element code when that element appears.
 *
 * Webpack:
 * - Using webpackChunkName "el.[request]" creates chunk names like:
 *   theme.el.card-product.js (chunkFilename is theme.[name].js?v=[contenthash])
 */
function setupCustomElementsAutoload() {
  const defined = new Set();
  const missing = new Set();
  const inFlight = new Set();

  // CSS styled log prefix for warnings about unknown custom elements
  const warnPrefix = "%c[Stencil/Element]";
  const warnStyle =
    "background:#ff9800;color:#000;border-radius:3px;font-weight:bold";

  /**
   * Dynamic import for element module.
   *
   * Convention:
   * - If the DOM contains <card-product>, we expect ./elements/card-product.js to exist.
   */
  const importElementModule = (tag) =>
    import(
      /* webpackChunkName: "el.[request]" */
      `./elements/${tag}`
    );

  const isCustomElementTag = (tag) => tag.includes("-");

  /**
   * Define an element if we haven't already.
   *
   * Failure handling distinguishes two cases:
   * - Genuinely missing module (webpack "Cannot find module" /
   *   MODULE_NOT_FOUND): remembered in `missing` so we warn once and never
   *   retry, since retrying can't help.
   * - Transient load failure (network blip fetching the async chunk):
   *   warned but NOT blacklisted, so the next DOM mutation scan can retry.
   */
  const isModuleNotFoundError = (error) =>
    error?.code === "MODULE_NOT_FOUND" ||
    /Cannot find module/i.test(String(error?.message ?? error));

  const ensureElementDefined = async (tag) => {
    const tagName = tag.toLowerCase();
    if (!isCustomElementTag(tagName)) return;
    if (defined.has(tagName) || customElements.get(tagName)) return;
    if (missing.has(tagName) || inFlight.has(tagName)) return;

    inFlight.add(tagName);
    try {
      const mod = await importElementModule(tagName);
      if (!customElements.get(tagName)) {
        customElements.define(tagName, mod.default);
      }
      defined.add(tagName);
    } catch (e) {
      if (isModuleNotFoundError(e)) {
        missing.add(tagName);
        console.warn(
          warnPrefix,
          warnStyle,
          `Custom element <${tagName}> found in DOM but no matching module exists in /elements/${tagName}.js`
        );
      } else {
        console.warn(
          warnPrefix,
          warnStyle,
          `Custom element <${tagName}> module failed to load; it will be retried on the next DOM scan.`,
          e
        );
      }
    } finally {
      // Always clear the in-flight marker: transient failures stay retryable.
      inFlight.delete(tagName);
    }
  };

  /**
   * Scan a root node for custom elements.
   */
  const scanForCustomTags = (root) => {
    const tags = new Set();
    root.querySelectorAll("*").forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (isCustomElementTag(tag)) tags.add(tag);
    });
    return tags;
  };

  // Initial scan (first paint DOM)
  (async () => {
    const used = scanForCustomTags(document);
    await Promise.all(Array.from(used).map(ensureElementDefined));
  })();

  /**
   * MutationObserver:
   * Covers injected HTML after load (cart drawer, section rendering, etc.)
   * We look at:
   * - the added node itself
   * - all descendants of the added node
   */
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;

        const tag = node.tagName.toLowerCase();
        if (isCustomElementTag(tag)) ensureElementDefined(tag);

        node.querySelectorAll?.("*").forEach((el) => {
          const t = el.tagName.toLowerCase();
          if (isCustomElementTag(t)) ensureElementDefined(t);
        });
      }
    }
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Optional return if anything wants to trigger manual loads later.
  return { ensureElementDefined };
}

/**
 * Register "core" extensions eagerly.
 *
 * We still use require.context for the lightweight, site-wide extensions.
 *
 * Important:
 * - This context regex intentionally EXCLUDES heavy/contextual extensions that
 *   are loaded via `registerExtensionLoader` + `ensureExtension`.
 * - If you widen this regex, webpack will bundle the excluded extensions into
 *   the main bundle and you will lose ext.* chunking.
 */
function registerExtensions(stencil) {
  const extensionsContext = require.context(
    "./extensions",
    false,
    /^(?!.*(?:propositions-manager|shopify-storefront-api)\.js$).*\.js$/
  );

  /**
   * Convert a file path like "./country-detect.js" into "CountryDetect"
   * to match extension naming conventions used by Stencil.
   */
  const fileToClassName = (file) =>
    file
      .replace("./", "")
      .replace(".js", "")
      .split("-")
      .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
      .join("");

  extensionsContext.keys().forEach((file) => {
    const moduleName = fileToClassName(file);
    const mod = extensionsContext(file);
    const ExtClass = mod.default;

    // Most extensions default to `static autoregister = true`
    if (ExtClass?.autoregister) {
      stencil.registerExtension(new ExtClass(moduleName));
    }
  });
}

/**
 * Legacy globals.
 * Keep these minimal and avoid adding more as we modernise.
 */
function setupGlobals() {
  window.debounce = function (fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}

/**
 * Boot sequence
 *
 * Order matters:
 * - Setup Stencil instance first
 * - Register vendor + extension loaders before anything tries to ensure them
 * - Expose statics for legacy calls (Stencil.* on instance)
 * - Setup element autoloading before extensions start manipulating DOM
 * - Register core extensions
 * - Setup globals
 * - Finally, run stencil.init() which drains callback queues and initialises extensions
 */
function boot() {
  const stencil = setupStencil();

  registerVendors(stencil);
  registerExtensionLoaders(stencil);

  exposeStencilStatics(Stencil, stencil);

  setupCustomElementsAutoload();
  registerExtensions(stencil);

  setupGlobals();

  stencil.init();
}

boot();
